(() => {
  if (customElements.get('sc-cart-progress-bar')) return;

  let synchronizationPromise = null;
  let pendingSynchronization = null;
  let externalFallbackTimer = null;
  let fallbackRenderInProgress = false;

  const cartRoute = () => `${window.Shopify?.routes?.root || window.routes?.root_url || '/'}cart.js`;
  const cartAddRoute = () => window.routes?.cart_add_url || '/cart/add.js';
  const cartUpdateRoute = () => window.routes?.cart_update_url || '/cart/update.js';
  const progressBars = () => Array.from(document.querySelectorAll('sc-cart-progress-bar'));
  const cartDrawer = () => document.querySelector('sc-cart-drawer');

  const isGiftItem = (item) => {
    const value = item?.properties?._gift;
    return value === true || (typeof value === 'string' && value.toLowerCase() === 'true');
  };

  const fetchCart = async () => {
    const response = await fetch(cartRoute(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error(`[sc-cart-progress-bar] cart.js failed (${response.status})`);
    return response.json();
  };

  const eligibleTotal = (cart) =>
    (Array.isArray(cart?.items) ? cart.items : []).reduce((total, item) => {
      if (isGiftItem(item)) return total;
      const linePrice = Number(item.final_line_price);
      return total + (Number.isFinite(linePrice) ? linePrice : 0);
    }, 0);

  const readGiftGoals = () => {
    const goals = new Map();

    progressBars().forEach((progressBar) => {
      progressBar.querySelectorAll('[data-gift-variant-id][data-goal-cents]').forEach((goal) => {
        const variantId = Number(goal.dataset.giftVariantId);
        const goalCents = Number(goal.dataset.goalCents);

        if (!Number.isFinite(variantId) || variantId <= 0 || !Number.isFinite(goalCents)) return;

        const existingGoal = goals.get(variantId);
        if (existingGoal === undefined || goalCents < existingGoal) goals.set(variantId, goalCents);
      });
    });

    return goals;
  };

  const snapshotFromCart = (cart) => ({
    totalCents: eligibleTotal(cart),
    giftItems: (Array.isArray(cart?.items) ? cart.items : [])
      .filter(isGiftItem)
      .map((item) => ({
        key: item.key,
        variantId: Number(item.variant_id),
        quantity: Number(item.quantity) || 0,
        element: null,
      }))
      .filter((item) => item.key && Number.isFinite(item.variantId) && item.variantId > 0),
  });

  const readRenderedSnapshot = () => {
    const progressBar = progressBars()[0];

    if (!progressBar) return null;

    const drawer = cartDrawer();
    const giftItems = Array.from(drawer?.querySelectorAll('[data-sc-cart-gift]') || [])
      .map((element) => ({
        key: element.dataset.scCartKey,
        variantId: Number(element.dataset.scCartVariantId),
        quantity: Number(element.dataset.scCartQuantity) || 0,
        element,
      }))
      .filter((item) => item.key && Number.isFinite(item.variantId) && item.variantId > 0);

    return {
      totalCents: Number(progressBar.dataset.eligibleTotalCents) || 0,
      giftItems,
    };
  };

  const updateProgressBars = (totalCents) => {
    progressBars().forEach((progressBar) => progressBar.updateProgress(totalCents));
  };

  const mutationSections = () => {
    const drawer = cartDrawer();
    const sections =
      typeof drawer?.getSectionsToRender === 'function'
        ? drawer
            .getSectionsToRender()
            .map((section) => section.id)
            .filter(Boolean)
        : [];

    if (!sections.length) return {};

    return {
      sections,
      sections_url: window.location.pathname,
    };
  };

  const requestJson = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    });
    const payload = await response.json().catch(() => ({}));

    if (!response.ok || payload.errors || payload.status) {
      throw new Error(
        payload.description || payload.message || `[sc-cart-progress-bar] Cart mutation failed (${response.status})`
      );
    }

    return payload;
  };

  const announceCartMutation = () => {
    document.dispatchEvent(
      new CustomEvent('cart:changed', { detail: { source: 'sc-cart-progress-bar' } })
    );

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sc-cart-progress-bar' });
    }
  };

  const setGiftGoalsLoading = (variantIds, isLoading) => {
    const ids = new Set(Array.from(variantIds, Number));

    progressBars().forEach((progressBar) => {
      progressBar.querySelectorAll('[data-gift-variant-id]').forEach((goal) => {
        if (ids.has(Number(goal.dataset.giftVariantId))) {
          goal.classList.toggle('is-syncing', isLoading);
        }
      });
    });
  };

  const setPendingRemoval = (items, isPending) => {
    items.forEach((item) => {
      const element =
        item.element ||
        Array.from(cartDrawer()?.querySelectorAll('[data-sc-cart-key]') || []).find(
          (candidate) => candidate.dataset.scCartKey === item.key
        );

      element?.classList.toggle('is-pending-gift-removal', isPending);
    });
  };

  const setDrawerBusy = (isBusy) => {
    const drawer = cartDrawer();

    if (!drawer) return;

    drawer.toggleAttribute('data-sc-cart-gift-syncing', isBusy);

    const root = drawer.querySelector('#ScCartDrawer');
    if (isBusy) {
      root?.setAttribute('aria-busy', 'true');
    } else {
      root?.removeAttribute('aria-busy');
    }

    const controls = drawer.querySelectorAll(
      '.sc-cart-drawer__checkout, [data-sc-cart-remove], [data-sc-cart-quantity-action], [data-sc-cart-quantity-input], [data-sc-cart-upsell-add]'
    );

    if (isBusy) {
      controls.forEach((control) => {
        if (control.disabled) return;
        control.disabled = true;
        control.dataset.scCartGiftSyncDisabled = 'true';
      });
      return;
    }

    drawer.querySelectorAll('[data-sc-cart-gift-sync-disabled="true"]').forEach((control) => {
      control.disabled = false;
      delete control.dataset.scCartGiftSyncDisabled;
    });
  };

  const applyMutationSections = async (payload) => {
    const drawer = cartDrawer();
    const drawerSection = payload?.sections?.['sc-cart-drawer'];

    if (drawerSection && typeof drawer?.updateRenderedSections === 'function') {
      drawer.updateRenderedSections(payload.sections, { source: 'sc-cart-progress-bar' });
      return;
    }

    if (typeof drawer?.refresh !== 'function') return;

    fallbackRenderInProgress = true;
    try {
      await drawer.refresh();
    } finally {
      fallbackRenderInProgress = false;
    }
  };

  const mutateCart = async (url, body) => {
    const payload = await requestJson(url, { ...body, ...mutationSections() });
    await applyMutationSections(payload);
    return payload;
  };

  const giftDelta = (snapshot, giftGoals) => {
    const desiredGiftIds = new Set(
      Array.from(giftGoals.entries())
        .filter(([, goalCents]) => snapshot.totalCents >= goalCents)
        .map(([variantId]) => variantId)
    );
    const retainedGiftIds = new Set();
    const updates = {};
    const removals = [];

    snapshot.giftItems.forEach((item) => {
      if (!giftGoals.has(item.variantId)) return;

      const shouldKeep = desiredGiftIds.has(item.variantId) && !retainedGiftIds.has(item.variantId);

      if (!shouldKeep) {
        updates[item.key] = 0;
        removals.push(item);
        return;
      }

      retainedGiftIds.add(item.variantId);
      if (item.quantity !== 1) updates[item.key] = 1;
    });

    const additions = Array.from(desiredGiftIds).filter(
      (variantId) => !retainedGiftIds.has(variantId)
    );

    return { additions, removals, updates };
  };

  const synchronizeGifts = async ({ snapshot = null, forceFetch = false } = {}) => {
    if (!progressBars().length) return;

    const giftGoals = readGiftGoals();
    const resolvedSnapshot = forceFetch ? snapshotFromCart(await fetchCart()) : snapshot || readRenderedSnapshot();

    if (!resolvedSnapshot || !giftGoals.size) return;

    updateProgressBars(resolvedSnapshot.totalCents);

    const { additions, removals, updates } = giftDelta(resolvedSnapshot, giftGoals);
    const hasUpdates = Object.keys(updates).length > 0;

    if (!hasUpdates && !additions.length) return;

    const affectedGiftIds = new Set([
      ...additions,
      ...removals.map((item) => item.variantId),
      ...resolvedSnapshot.giftItems
        .filter((item) => updates[item.key] === 1)
        .map((item) => item.variantId),
    ]);
    const drawer = cartDrawer();
    let cartMutated = false;

    drawer?.clearErrors?.();
    setPendingRemoval(removals, true);
    setGiftGoalsLoading(affectedGiftIds, true);
    setDrawerBusy(true);

    try {
      if (hasUpdates) {
        await mutateCart(cartUpdateRoute(), { updates });
        cartMutated = true;

        if (additions.length) {
          setGiftGoalsLoading(affectedGiftIds, true);
          setDrawerBusy(true);
        }
      }

      if (additions.length) {
        await mutateCart(cartAddRoute(), {
          items: additions.map((variantId) => ({
            id: variantId,
            quantity: 1,
            properties: { _gift: 'true' },
          })),
        });
        cartMutated = true;
      }
    } catch (error) {
      setPendingRemoval(removals, false);
      const customerMessage = drawer?.dataset.cartUpdateError;
      if (customerMessage) drawer.showError?.(customerMessage);
      throw error;
    } finally {
      setGiftGoalsLoading(affectedGiftIds, false);
      setDrawerBusy(false);
      if (cartMutated) announceCartMutation();
    }
  };

  const queueGiftSynchronization = (options = {}) => {
    pendingSynchronization = {
      snapshot: options.snapshot || pendingSynchronization?.snapshot || null,
      forceFetch: Boolean(options.forceFetch || pendingSynchronization?.forceFetch),
    };

    if (synchronizationPromise) return synchronizationPromise;

    synchronizationPromise = (async () => {
      await Promise.resolve();

      while (pendingSynchronization) {
        const synchronization = pendingSynchronization;
        pendingSynchronization = null;

        try {
          await synchronizeGifts(synchronization);
        } catch (error) {
          console.error('[sc-cart-progress-bar]', error);
        }
      }
    })().finally(() => {
      synchronizationPromise = null;
      if (pendingSynchronization) queueGiftSynchronization();
    });

    return synchronizationPromise;
  };

  class ScCartProgressBar extends HTMLElement {
    constructor() {
      super();
      this.handleCartChange = this.handleCartChange.bind(this);
      this.handleCartRendered = this.handleCartRendered.bind(this);
    }

    connectedCallback() {
      if (this.dataset.scCartProgressInitialized === 'true') return;

      this.dataset.scCartProgressInitialized = 'true';
      document.addEventListener('cart:changed', this.handleCartChange);
      document.addEventListener('sc:cart-rendered', this.handleCartRendered);
      this.updateProgress(Number(this.dataset.eligibleTotalCents) || 0);
      customElements.whenDefined('sc-cart-drawer').then(() => {
        if (this.isConnected) {
          queueGiftSynchronization({ snapshot: readRenderedSnapshot() });
        }
      });
    }

    disconnectedCallback() {
      document.removeEventListener('cart:changed', this.handleCartChange);
      document.removeEventListener('sc:cart-rendered', this.handleCartRendered);
      window.clearTimeout(externalFallbackTimer);
      delete this.dataset.scCartProgressInitialized;
    }

    handleCartChange(event) {
      if (event.detail?.source === 'sc-cart-progress-bar') return;

      if (event.detail?.cart?.items) {
        queueGiftSynchronization({ snapshot: snapshotFromCart(event.detail.cart) });
        return;
      }

      if (
        event.detail?.source === 'sc-cart-drawer' ||
        event.detail?.source === 'sc-cart-drawer-upsell'
      ) {
        return;
      }

      window.clearTimeout(externalFallbackTimer);
      externalFallbackTimer = window.setTimeout(() => {
        queueGiftSynchronization({ forceFetch: true });
      }, 500);
    }

    handleCartRendered(event) {
      if (event.detail?.source === 'sc-cart-progress-bar' || fallbackRenderInProgress) return;

      window.clearTimeout(externalFallbackTimer);
      queueGiftSynchronization({ snapshot: readRenderedSnapshot() });
    }

    formatMoney(cents) {
      const currency = this.dataset.currency || 'EUR';
      const currencySymbol = this.dataset.currencySymbol || currency;
      const locale = this.dataset.locale || document.documentElement.lang || 'de';

      try {
        const amount = new Intl.NumberFormat(locale, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(cents / 100);

        return `${currencySymbol}${amount}`;
      } catch (error) {
        return `${currencySymbol}${(cents / 100).toFixed(2).replace('.', ',')}`;
      }
    }

    updateProgress(totalCents) {
      const goals = Array.from(this.querySelectorAll('[data-goal-cents]'))
        .map((goal) => ({ element: goal, amount: Number(goal.dataset.goalCents) }))
        .filter((goal) => Number.isFinite(goal.amount) && goal.amount > 0)
        .sort((first, second) => first.amount - second.amount);

      if (!goals.length) return;

      const maximum = goals[goals.length - 1].amount;
      const percentage = Math.min(100, Math.max(0, (totalCents / maximum) * 100));
      const nextGoal = goals.find((goal) => totalCents < goal.amount);
      const isComplete = !nextGoal;

      this.dataset.eligibleTotalCents = String(totalCents);
      this.style.setProperty('--sc-cart-shipping-progress', `${percentage}%`);
      this.classList.toggle('is-complete', isComplete);
      this.classList.toggle('is-reached', isComplete);

      const track = this.querySelector('[role="progressbar"]');
      track?.setAttribute('aria-valuenow', String(Math.round(percentage)));

      goals.forEach(({ element, amount }) => {
        element.classList.toggle('is-reached', totalCents >= amount);
      });

      const message = this.querySelector('[data-sc-cart-progress-message]');
      if (!message) return;

      const template = isComplete
        ? this.querySelector('template[data-sc-cart-completion-message]')
        : this.querySelector(
            `template[data-sc-cart-goal-message][data-goal-cents="${nextGoal.amount}"]`
          );

      if (!(template instanceof HTMLTemplateElement)) return;

      const remaining = nextGoal ? Math.max(0, nextGoal.amount - totalCents) : 0;
      message.innerHTML = template.innerHTML.replaceAll('%%money%%', this.formatMoney(remaining));
    }
  }

  customElements.define('sc-cart-progress-bar', ScCartProgressBar);
})();
